import {inject, injectable} from 'inversify';
import {EventMap, IEventManagerWithAsync, Listener} from '@fantasmagames/core/src/events/_interfaces/IEventManager';
import {IAsyncEventManager} from '@fantasmagames/core/src/events/_interfaces/IAsyncEventManager';
import {CoreDI} from '@fantasmagames/core/src/di/CoreDI';

/**
 * game events handler and dispatcher. represents the way of communication between modules
 * all the global events goes through this class (it should be injected into every module)
 */
@injectable()
export class EventManagerWithAsync implements IEventManagerWithAsync {

	protected _eventMap: EventMap = {};

	@inject(CoreDI.IAsyncEventManager)
	protected _asyncEventsManager: IAsyncEventManager;

	public on(id: string, callback: Function, owner: Object, priority: number = 0, data: any = null): void {
		this.addListenerAs(false, id, callback, owner, priority, data);
	}

	public off(id: string, owner: Object): void {
		this.removeListenerFrom(false, id, owner);
	}

	public hasListener(id: string, owner: Object): boolean {
		return this.hasListenerAt(false, id, owner);
	}

	public emit(id: string, ...args: any[]): void {
		const listeners: Listener[] = this._eventMap[id];

		if (listeners) {
			let length: number = listeners.length;
			for (let i = 0; i < length; i++) {
				const listener = listeners[i];
				listener.callback(...args);
				if (listeners.length !== length) {
					i--;
					length = listeners.length;
				}
			}
		}
	}

	/**
	 * emitting event after the call stack if cleaned (next tick)
	 * @param id event id
	 * @param args arguments to pass with event
	 */
	public emitDelayed(id: string, ...args: any[]): void {
		setTimeout(() => {
			this.emit(id, ...args);
		}, 0);
	}

	public onAsync(id: string, callback: Function, owner: Object, priority: number = 0, data: any = null): void {
		this.addListenerAs(true, id, callback, owner, priority, data);
	}

	public offAsync(id: string, owner: Object): void {
		this.removeListenerFrom(true, id, owner);
	}

	public hasAsyncListener(id: string, owner: Object): boolean {
		return this.hasListenerAt(true, id, owner);
	}

	private addListenerAs(isAsync: boolean, id: string, callback: Function, owner: Object, priority: number, data: any): void {
		const currentEventsMap: EventMap = this.getAppropriateEventMap(isAsync);

		if (!currentEventsMap[id]) {
			currentEventsMap[id] = [];
		}

		currentEventsMap[id].push({callback, owner, priority, data});
		currentEventsMap[id].sort((a, b) => {
			return b.priority - a.priority;
		});
	}

	private removeListenerFrom(isAsync: boolean, id: string, owner: Object): void {
		const currentEventsMap: EventMap = this.getAppropriateEventMap(isAsync);
		const listeners: Listener[] = currentEventsMap[id];
		const index: number = listeners.findIndex((listener: Listener) => {
			return listener.owner === owner;
		});

		if (index > -1) {
			listeners.splice(index, 1);
		}
	}

	private hasListenerAt(isAsync: boolean, id: string, owner: Object): boolean {
		const currentEventsMap: EventMap = this.getAppropriateEventMap(isAsync);

		const listeners: Listener[] = currentEventsMap[id];
		const listener: Listener = listeners.find((listener: Listener) => {
			return listener.owner === owner;
		});

		return listener !== undefined;
	}

	private getAppropriateEventMap(isAsync: boolean): EventMap {
		return isAsync ? this._asyncEventsManager._asyncEventMap : this._eventMap;
	}
}


// WEBPACK FOOTER //
// ./@fantasmagames/core/src/events/EventManagerWithAsync.ts